import { useAsyncData, useState } from '#imports'
import { computed, onMounted, watch } from 'vue'
import type { LocationQuery } from 'vue-router'

import type { BuybackOffer } from '@backmarket/http-api/src/api-models/BuybackOffer'
import type { Price } from '@backmarket/http-api/src/api-models/Price'
import type { HttpApiError } from '@backmarket/http-api/src/utils/HttpApiError'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { calculateReduction } from '@backmarket/utils/math/calculateReduction'

import { useBuybackEnabled } from '../../../../composables/useBuybackEnabled/useBuybackEnabled'
import { SWAP_INFO_MESSAGE } from '../../../../constants'
import {
  buybackOfferToLegacyOffer,
  legacyOfferToBuybackOffer,
  offerV1ToBuybackOffer,
} from '../../adapters/buybackOffer/buybackOffer'
import type { GetOfferV1Response } from '../../api/types'

import { useOfferLocalStorage } from './offerLocalStorage'

export type DeleteBuybackOfferResult =
  | { success: true; error?: undefined }
  | { success: false; error: HttpApiError }

export function useBuybackOffer() {
  const { market } = useMarketplace()
  const { isBuybackEnabled, isCartStorageEnabled } = useBuybackEnabled()

  const $httpFetch = useHttpFetch()
  const i18n = useI18n()
  const logger = useLogger()
  const offerLocalStorage = !isCartStorageEnabled
    ? useOfferLocalStorage()
    : null

  const defaultBuybackOfferPrice: Price = {
    amount: '140',
    currency: market.defaultCurrency,
  }

  const freePrice: Price = {
    amount: '0',
    currency: market.defaultCurrency,
  }

  const fetchedFromCart = useState<boolean>(
    'buyback-offer-from-cart',
    () => false,
  )

  const fetchedFromLocalStorage = useState<boolean>(
    'buyback-offer-from-local-storage',
    () => false,
  )

  const buybackOffer = useState<GetOfferV1Response | null>(
    'buyback-offer',
    () => null,
  )
  const buybackOfferPayload = useState<Record<string, unknown> | null>(
    'buyback-offer-payload',
    () => null,
  )
  const hasNoOffer = useState<boolean>('buyback-has-no-offer', () => false)

  const cartPriceWithSwapDiscount = useState<number | null>(
    'buyback-cart-price-with-swap-discount',
    () => null,
  )

  const { data: swapOfferFromCart, execute } = useAsyncData(
    async () => {
      return $httpFetch('/bm/cart/swap')
    },
    { immediate: false },
  )

  const hasBuybackOffer = computed(() => buybackOffer.value !== null)

  const hasBuybackOfferAvailable = computed(
    () => buybackOffer.value !== null || hasNoOffer.value,
  )

  const legacyOffer = computed<BuybackOffer | null>(() => {
    if (!buybackOffer.value) return null

    return offerV1ToBuybackOffer(
      buybackOffer.value,
      buybackOfferPayload.value,
      i18n.currencySign,
    )
  })

  const cartSwapAdPrice = computed<Price | undefined>(() => {
    if (cartPriceWithSwapDiscount.value === null) return undefined

    return {
      amount: cartPriceWithSwapDiscount.value.toString(),
      currency: market.defaultCurrency,
    }
  })

  function setCartPriceWithSwapDiscount(value: number | null) {
    cartPriceWithSwapDiscount.value = value
  }

  function setBuybackOffer(
    offer: GetOfferV1Response,
    payload: LocationQuery | Record<string, unknown>,
  ) {
    buybackOffer.value = offer
    buybackOfferPayload.value = payload
  }

  function resetBuybackOffer() {
    buybackOffer.value = null
    hasNoOffer.value = false
  }

  function restoreOfferFromLocalStorage() {
    if (!offerLocalStorage) return
    const storedOffer = offerLocalStorage.readOfferFromLocalStorage()
    if (storedOffer) {
      setBuybackOffer(storedOffer, {})
    }
  }

  async function deleteBuybackOffer(): Promise<DeleteBuybackOfferResult> {
    if (isCartStorageEnabled) {
      try {
        await $httpFetch('/bm/cart/swap', { method: 'DELETE' })
        resetBuybackOffer()
      } catch (err) {
        const deletedError = err as HttpApiError
        logger.error(SWAP_INFO_MESSAGE.DELETE_SWAP, {
          error: deletedError,
          owners: ['bot-squad-circularity-offer-front'],
        })

        return { success: false, error: deletedError }
      }
    } else if (offerLocalStorage) {
      offerLocalStorage.removeOfferFromLocalStorage()
      resetBuybackOffer()
    }

    return { success: true }
  }

  function resetHasNoOffer() {
    hasNoOffer.value = false
  }

  function getDiscountedPrice(initialPrice: Price | null) {
    if (!initialPrice) {
      return freePrice
    }
    const finalPrice = calculateReduction(
      initialPrice,
      buybackOffer.value?.listing.price ?? defaultBuybackOfferPrice,
    )

    return finalPrice && parseFloat(finalPrice.amount) >= 0
      ? finalPrice
      : freePrice
  }

  async function saveBuybackOffer(
    offer: GetOfferV1Response,
    payload: LocationQuery | Record<string, unknown>,
  ) {
    if (isCartStorageEnabled) {
      await $httpFetch('/bm/cart/swap', {
        method: 'POST',
        body: buybackOfferToLegacyOffer(offer, payload, i18n.currencySign),
      })
    } else if (offerLocalStorage) {
      offerLocalStorage.writeOfferToLocalStorage(offer)
    }

    setBuybackOffer(offer, payload)
  }

  function saveHasNoOffer() {
    hasNoOffer.value = true
  }

  function getDiscountedPriceForProductCard(initialPrice: Price) {
    if (initialPrice && buybackOffer.value?.listing.price) {
      const finalPrice = calculateReduction(
        initialPrice,
        buybackOffer.value?.listing.price,
      )

      return finalPrice && parseFloat(finalPrice.amount) >= 0
        ? finalPrice
        : freePrice
    }

    return undefined
  }

  watch(swapOfferFromCart, (newSwapOfferFromCart) => {
    if (newSwapOfferFromCart && !buybackOffer.value) {
      setBuybackOffer(
        legacyOfferToBuybackOffer(newSwapOfferFromCart),
        newSwapOfferFromCart.diagnostic_payload,
      )
    }
  })

  onMounted(() => {
    if (!isBuybackEnabled) return

    if (!fetchedFromCart.value && !fetchedFromLocalStorage.value) {
      if (isCartStorageEnabled) {
        void execute()
        fetchedFromCart.value = true
      } else {
        restoreOfferFromLocalStorage()
        fetchedFromLocalStorage.value = true
      }
    }
  })

  return {
    buybackOffer,
    buybackOfferPayload,
    hasBuybackOffer,
    hasBuybackOfferAvailable,
    legacyOffer,
    cartSwapAdPrice,
    setCartPriceWithSwapDiscount,
    saveBuybackOffer,
    saveHasNoOffer,
    getDiscountedPrice,
    getDiscountedPriceForProductCard,
    deleteBuybackOffer,
    resetBuybackOffer,
    resetHasNoOffer,
  }
}
